/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { WorkflowStateRequest } from '../models/workflow-state-request';
import { WorkflowStateResponse } from '../models/workflow-state-response';


/**
 * Persist messages and workflow states to CosmosDB
 */
@Injectable({
  providedIn: 'root',
})
export class StatePersistenceApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation saveWorkflowState
   */
  static readonly SaveWorkflowStatePath = '/langgraph/workflow_state';

  /**
   * Save message or workflow state to CosmosDB.
   *
   * Persists user messages, assistant messages, or workflow states to CosmosDB
   * as part of conversation history. This endpoint supports three use cases:
   *
   * ## Use Case 1: Save User Message
   *
   * Called automatically before sending message to workflow.
   *
   * **Client Usage** (`langgraph_client.py` lines 285-286):
   * ```python
   * if not user_input and self.enable_history:
   *     await self.save_message_to_workflow_state(messages[0], "user_message")
   * ```
   *
   * ## Use Case 2: Save Assistant Message
   *
   * Called after workflow completes to save final response.
   *
   * **Client Usage** (`langgraph_client.py` lines 360-370):
   * ```python
   * if self.enable_history and final_response and not workflow_paused:
   *     assistant_message = {
   *         "role": "assistant",
   *         "content": final_response,
   *         "id": str(uuid.uuid4())
   *     }
   *     await self.save_message_to_workflow_state(assistant_message, "assistant_message")
   * ```
   *
   * ## Use Case 3: Save Workflow State
   *
   * Called automatically when receiving intermediate_state or final_state updates.
   *
   * **Client Usage** (`langgraph_client.py` lines 308-321):
   * ```python
   * elif update_type == "intermediate_state":
   *     data = update.get("data", {})
   *     if "workflow_state" in data:
   *         workflow_state.update(data["workflow_state"])
   *         await self.save_workflow_state(data["workflow_state"], "intermediate_state")
   *
   * elif update_type == "final_state":
   *     data = update.get("data", {})
   *     if "workflow_state" in data:
   *         await self.save_workflow_state(data["workflow_state"], "final_state")
   * ```
   *
   * **Note**: Either provide `message` + `message_type` OR `workflow_state` + `update_type`,
   * not both.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `saveWorkflowState()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveWorkflowState$Response(params: {
    context?: HttpContext
    body: WorkflowStateRequest
  }
): Observable<StrictHttpResponse<WorkflowStateResponse>> {

    const rb = new RequestBuilder(this.rootUrl, StatePersistenceApiService.SaveWorkflowStatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkflowStateResponse>;
      })
    );
  }

  /**
   * Save message or workflow state to CosmosDB.
   *
   * Persists user messages, assistant messages, or workflow states to CosmosDB
   * as part of conversation history. This endpoint supports three use cases:
   *
   * ## Use Case 1: Save User Message
   *
   * Called automatically before sending message to workflow.
   *
   * **Client Usage** (`langgraph_client.py` lines 285-286):
   * ```python
   * if not user_input and self.enable_history:
   *     await self.save_message_to_workflow_state(messages[0], "user_message")
   * ```
   *
   * ## Use Case 2: Save Assistant Message
   *
   * Called after workflow completes to save final response.
   *
   * **Client Usage** (`langgraph_client.py` lines 360-370):
   * ```python
   * if self.enable_history and final_response and not workflow_paused:
   *     assistant_message = {
   *         "role": "assistant",
   *         "content": final_response,
   *         "id": str(uuid.uuid4())
   *     }
   *     await self.save_message_to_workflow_state(assistant_message, "assistant_message")
   * ```
   *
   * ## Use Case 3: Save Workflow State
   *
   * Called automatically when receiving intermediate_state or final_state updates.
   *
   * **Client Usage** (`langgraph_client.py` lines 308-321):
   * ```python
   * elif update_type == "intermediate_state":
   *     data = update.get("data", {})
   *     if "workflow_state" in data:
   *         workflow_state.update(data["workflow_state"])
   *         await self.save_workflow_state(data["workflow_state"], "intermediate_state")
   *
   * elif update_type == "final_state":
   *     data = update.get("data", {})
   *     if "workflow_state" in data:
   *         await self.save_workflow_state(data["workflow_state"], "final_state")
   * ```
   *
   * **Note**: Either provide `message` + `message_type` OR `workflow_state` + `update_type`,
   * not both.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `saveWorkflowState$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveWorkflowState(params: {
    context?: HttpContext
    body: WorkflowStateRequest
  }
): Observable<WorkflowStateResponse> {

    return this.saveWorkflowState$Response(params).pipe(
      map((r: StrictHttpResponse<WorkflowStateResponse>) => r.body as WorkflowStateResponse)
    );
  }

}
